import { getCommerceUrlBase } from "@/utils/functions/commerce";
import { useAuth } from "@/components/AuthProvider";
import {
  QueryParams,
  buildSafeUrlWithBase,
} from "@/utils/functions/buildSafeUrl";
import { useAuthBaseURL } from "@/hooks/useAuthBaseURL";
import useResolvedMarketplaceAttribution from "@/hooks/useResolvedMarketplaceAttribution";

export enum CommercePath {
  PaymentRedirect = "/lms/payment_page_redirect/",
}

/**
 * Builds a URL for Commerce Coordinator-related actions, with support for user authentication redirection.
 *
 * @param {CommercePath} path - The Commerce Coordinator-specific path to be appended to the commerce base URL. 9please update the enum to add more)
 * @param {QueryParams} [qps] - An optional set of query parameters to include in the URL.
 * @param {"login" | "register"} [loggedOutAuthAction="login"] - The authentication action ("login" or "register") to redirect users to when they are logged out.
 * @returns {string} - The constructed Commerce Coordinator URL or an authentication URL with a "next" parameter pointing to the commerce URL if the user is not logged in.
 */
export function useBuildCommerceUrl(
  path: CommercePath,
  qps?: QueryParams,
  loggedOutAuthAction: "login" | "register" = "login",
  byPassAuth: boolean = false, // this is used to generate the correct redirect url for modal auth and avoid redirecting to the auth page
): string {
  const attributionParams = Object.fromEntries(
    useResolvedMarketplaceAttribution().entries(),
  );
  const coordinator = buildSafeUrlWithBase(getCommerceUrlBase(), path, {
    ...attributionParams,
    ...qps,
  });

  const authBase = useAuthBaseURL();
  const { isLoggedIn } = useAuth();

  if (isLoggedIn || byPassAuth) {
    return coordinator;
  }

  return buildSafeUrlWithBase(authBase, loggedOutAuthAction, {
    ...attributionParams,
    next: coordinator,
  });
}
